import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { Config } from "src/app/_store/config";

/**
 * Bank-detail row returned by the JSW Aadhaar gateway `getApiRequestResultsData`
 * IFSC lookup (one entry of `apiDataArray`). Only fields used for bank auto-fill
 * are typed; the rest are accessible via the index signature.
 */
export interface JswBankDetailsRow {
  BANK_NAME?: string;
  BANK_ADDRESS?: string;
  BANK_BRANCH?: string;
  BANK_CITY?: string;
  BANK_STATE_DESC?: string;
  BANK_STATE_CODE?: string;
  BANK_SWIFT_CODE?: string | null;
  BANK_COUNTRY?: string;
  IFSC_CODE?: string;
  [key: string]: unknown;
}

/** Form keys populated from IFSC lookup. */
export type IfscBankFieldMap = Record<string, string>;

/**
 * Thrown when the gateway responds successfully but has no bank record for the
 * requested IFSC (`apiDataArray: []`). Distinct from transport/auth failures so
 * callers can surface a "not found" modal instead of a generic error.
 */
export class IfscNotFoundError extends Error {
  constructor(message = "IFSC code not found") {
    super(message);
    this.name = "IfscNotFoundError";
  }
}

@Injectable({ providedIn: "root" })
export class IfscLookupService {
  constructor(private appHttp: HttpClient) {}

  lookup(ifsc: string): Observable<IfscBankFieldMap> {
    const code = (ifsc || "").trim().toUpperCase();
    if (!code) {
      return throwError(() => new Error("IFSC code is required."));
    }
    return this.appHttp
      .post<{ data?: { apiDataArray?: JswBankDetailsRow[] } }>(
        Config.urls.JSW_GATEWAY_BANK_DETAILS,
        { ifsc: code },
      )
      .pipe(
        map((resp) => {
          const data = (resp as any)?.data ?? resp;
          const arr =
            data && Array.isArray((data as any).apiDataArray)
              ? ((data as any).apiDataArray as JswBankDetailsRow[])
              : [];
          const row = arr[0];
          if (!row) {
            throw new IfscNotFoundError();
          }
          return mapJswRowToBankFields(row);
        }),
        catchError((err: HttpErrorResponse | Error) => {
          if (err instanceof Error && !(err instanceof HttpErrorResponse)) {
            // "IFSC code not found" / "IFSC code is required" — pass through.
            return throwError(() => err);
          }
          const msg =
            err instanceof HttpErrorResponse
              ? err.message
              : (err as Error).message || "Unable to fetch bank details.";
          return throwError(() => new Error(`IFSC lookup failed: ${msg}`));
        }),
      );
  }
}

/** Maps a JSW gateway bank-detail row to change-request bank form keys. */
export function mapJswRowToBankFields(
  row: JswBankDetailsRow,
): IfscBankFieldMap {
  const str = (v: unknown): string =>
    v === null || v === undefined ? "" : String(v).trim();
  return {
    BANK_NAME: str(row.BANK_NAME),
    BANK_ADDRESS: str(row.BANK_ADDRESS),
    BANK_BRANCH: str(row.BANK_BRANCH),
    BANK_CITY: str(row.BANK_CITY),
    BANK_STATE_DESC: str(row.BANK_STATE_DESC),
    BANK_STATE_CODE: str(row.BANK_STATE_CODE),
    BANK_SWIFT_CODE: str(row.BANK_SWIFT_CODE),
    BANK_COUNTRY: str(row.BANK_COUNTRY),
  };
}
